import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiClient } from '../api/apiConfig';
import { BookOpenIcon, PencilIcon, TrashIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { Button, Alert, Spinner, Badge } from './ui';
import BookForm from './forms/BookForm';
import { Book } from '../types/book';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../design/tokens';

const BookList: React.FC = () => {
  const [books, setBooks] = useState<Book[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState<Book | undefined>(undefined);
  const [hoveredBookId, setHoveredBookId] = useState<number | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/api/books/');
      setBooks(response.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load books data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEdit = (e: React.MouseEvent, book: Book) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedBook(book);
    setIsEditModalOpen(true);
  };

  const handleDelete = async (e: React.MouseEvent, bookId: number) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await apiClient.delete(`/api/books/${bookId}/`);

      if (response.status >= 200 && response.status < 300) {
        setBooks(books.filter(book => book.id !== bookId));
      } else {
        const errorData = response.data;
        console.error('Delete failed:', errorData);
        alert('Failed to delete book: ' + (errorData?.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error deleting book:', error);
      alert('Failed to delete book');
    }
  };

  const getStatusVariant = (status: string): 'success' | 'info' | 'default' => {
    switch (status) {
      case 'read_and_digested':
        return 'success';
      case 'read':
        return 'success';
      case 'in_progress':
        return 'info';
      case 'not_started':
        return 'default';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '128px',
      }}>
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">{error}</Alert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING[16] }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(1, 1fr)',
        gap: SPACING[16],
      }}>
        {books.map((book) => (
          <Link
            key={book.id}
            to={`/books/${book.id}`}
            style={{
              display: 'block',
              backgroundColor: COLORS.bg.panel,
              borderRadius: RADIUS,
              overflow: 'hidden',
              boxShadow: hoveredBookId === book.id
                ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                : '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
              transition: 'box-shadow 200ms ease-in-out',
              textDecoration: 'none',
            }}
            onMouseEnter={() => setHoveredBookId(book.id)}
            onMouseLeave={() => setHoveredBookId(null)}
          >
            <div style={{ padding: SPACING[16] }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[12],
                }}>
                  <BookOpenIcon style={{
                    height: '24px',
                    width: '24px',
                    color: COLORS.accent.default,
                  }} />
                  <div>
                    <h3 style={{
                      fontSize: TYPOGRAPHY.size.lg,
                      fontWeight: TYPOGRAPHY.weight.medium,
                      color: COLORS.text.primary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      margin: 0,
                    }}>{book.title}</h3>
                    <p style={{
                      fontSize: TYPOGRAPHY.size.sm,
                      color: COLORS.text.secondary,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      margin: 0,
                    }}>{book.author}</p>
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: SPACING[8],
                }}>
                  <Badge variant={getStatusVariant(book.status)} size="sm">
                    {book.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </Badge>
                  <Button
                    onClick={(e) => handleEdit(e, book)}
                    variant="ghost"
                    size="sm"
                    style={{ padding: SPACING[4], height: 'auto' }}
                  >
                    <PencilIcon style={{
                      height: '20px',
                      width: '20px',
                      color: COLORS.text.secondary,
                    }} />
                  </Button>
                  <Button
                    onClick={(e) => handleDelete(e, book.id)}
                    variant="ghost"
                    size="sm"
                    style={{ padding: SPACING[4], height: 'auto' }}
                  >
                    <TrashIcon style={{
                      height: '20px',
                      width: '20px',
                      color: COLORS.status.error,
                    }} />
                  </Button>
                  <ChevronRightIcon style={{
                    height: '20px',
                    width: '20px',
                    color: COLORS.text.tertiary,
                  }} />
                </div>
              </div>

              {book.overall_summary && (
                <p style={{
                  marginTop: SPACING[8],
                  marginBottom: 0,
                  fontSize: TYPOGRAPHY.size.sm,
                  color: COLORS.text.secondary,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}>{book.overall_summary}</p>
              )}

              <div style={{
                marginTop: SPACING[8],
                fontSize: TYPOGRAPHY.size.sm,
                color: COLORS.text.secondary,
              }}>
                {book.chapters?.length || 0} chapter{(book.chapters?.length || 0) !== 1 ? 's' : ''}
              </div>
            </div>
          </Link>
        ))}
      </div>

      <BookForm
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedBook(undefined);
        }}
        onBookCreated={fetchData}
        initialBook={selectedBook}
        isEditMode={!!selectedBook}
      />
    </div>
  );
};

export default BookList;
